import { useState, useMemo, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import { Badge } from "./common/Badge";
import Button from "./common/Button";
import * as utils from "../utils/utils";
import {
  FileText,
  Download,
  ExternalLink,
  Heart,
  Eye,
  Scissors,
  Activity,
  Stethoscope,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";

const CONSENT_DOCUMENT_DEFS = [
  {
    id: 1,
    slug: "anesthesiaPostop",
    categoryKey: "anesthesia",
    size: "569.7 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_anestesia_y_analgesia_postoperatoria_c.pdf",
    icon: Activity,
  },
  {
    id: 2,
    slug: "bronchoscopy",
    categoryKey: "diagnostic",
    size: "629.8 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_broncoscopia.pdf",
    icon: Stethoscope,
  },
  {
    id: 3,
    slug: "cataractSurgery",
    categoryKey: "ophthalmology",
    size: "125.5 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital-sommer-consentimiento-informado-cirugia-cataratas.pdf",
    icon: Eye,
  },
  {
    id: 4,
    slug: "crosectomy",
    categoryKey: "vascularSurgery",
    size: "928.7 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_crosectomia.pdf",
    icon: Scissors,
  },
  {
    id: 5,
    slug: "diagnosisTreatment",
    categoryKey: "general",
    size: "731.2 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_diagnostico-y-o-tratamiento.pdf",
    icon: FileText,
  },
  {
    id: 6,
    slug: "electromyogram",
    categoryKey: "diagnostic",
    size: "828.1 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_electromiograma.pdf",
    icon: Activity,
  },
  {
    id: 7,
    slug: "limbAmputation",
    categoryKey: "surgery",
    size: "1.0 Mb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_intervencion_quirurgica_de_amputacion_de_miembros.pdf",
    icon: Scissors,
  },
  {
    id: 8,
    slug: "hivTest",
    categoryKey: "diagnostic",
    size: "476.9 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_prueba_diagnostica_de_h.i.v.pdf",
    icon: FileText,
  },
  {
    id: 9,
    slug: "bloodTransfusion",
    categoryKey: "hemotherapy",
    size: "689.2 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_hemoterapia.pdf",
    icon: Heart,
  },
  {
    id: 10,
    slug: "cardioRehab",
    categoryKey: "rehabilitation",
    size: "631.8 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_rehabilitacion_cardiorrespiratoria.pdf",
    icon: Heart,
  },
  {
    id: 11,
    slug: "stressTest",
    categoryKey: "cardiology",
    size: "649.1 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_ergometria_graduada.pdf",
    icon: Heart,
  },
  {
    id: 12,
    slug: "liverBiopsy",
    categoryKey: "diagnostic",
    size: "540.5 Kb",
    url: "https://www.argentina.gob.ar/sites/default/files/2018/10/hospital_sommer_consentimiento_informado_puncion_hepatica.pdf",
    icon: FileText,
  },
];

const PREDEFINED_CATEGORY_KEYS = Array.from(
  new Set(CONSENT_DOCUMENT_DEFS.map((doc) => doc.categoryKey))
).sort();

const CATEGORY_COLORS = {
  anesthesia: "bg-purple-100 text-purple-800",
  diagnostic: "bg-blue-100 text-blue-800",
  ophthalmology: "bg-green-100 text-green-800",
  vascularSurgery: "bg-red-100 text-red-800",
  general: "bg-gray-100 text-gray-800",
  surgery: "bg-orange-100 text-orange-800",
  hemotherapy: "bg-pink-100 text-pink-800",
  rehabilitation: "bg-indigo-100 text-indigo-800",
  cardiology: "bg-rose-100 text-rose-800",
  custom: "bg-yellow-100 text-yellow-800",
};

const PREDEFINED_DEFAULTS = {
  anesthesiaPostop: {
    title: "Anestesia y Analgesia Postoperatoria",
    description:
      "Consentimiento para procedimientos anestésicos y manejo del dolor postoperatorio",
  },
  bronchoscopy: {
    title: "Broncoscopía",
    description:
      "Procedimiento endoscópico para examinar las vías respiratorias",
  },
  cataractSurgery: {
    title: "Cirugía de Cataratas",
    description: "Intervención quirúrgica para el tratamiento de cataratas",
  },
  crosectomy: {
    title: "Crosectomía",
    description: "Procedimiento quirúrgico vascular",
  },
  diagnosisTreatment: {
    title: "Diagnóstico y/o Tratamiento",
    description:
      "Consentimiento general para procedimientos diagnósticos y terapéuticos",
  },
  electromyogram: {
    title: "Electromiograma",
    description: "Estudio de la actividad eléctrica muscular",
  },
  limbAmputation: {
    title: "Intervención Quirúrgica de Amputación de Miembros",
    description: "Procedimiento quirúrgico de amputación",
  },
  hivTest: {
    title: "Prueba Diagnóstica de H.I.V",
    description: "Consentimiento para prueba de detección de VIH",
  },
  bloodTransfusion: {
    title: "Práctica Transfusional",
    description: "Consentimiento para transfusión sanguínea",
  },
  cardioRehab: {
    title: "Programa de Rehabilitación Cardiorrespiratoria",
    description: "Programa de rehabilitación cardiovascular y respiratoria",
  },
  stressTest: {
    title: "Prueba de Ergometría Graduada",
    description: "Prueba de esfuerzo cardiovascular",
  },
  liverBiopsy: {
    title: "Punción Biopsia Hepática",
    description: "Procedimiento de biopsia hepática",
  },
};

const CATEGORY_LABEL_DEFAULTS = {
  anesthesia: "Anestesia",
  diagnostic: "Diagnóstico",
  ophthalmology: "Oftalmología",
  vascularSurgery: "Cirugía Vascular",
  general: "General",
  surgery: "Cirugía",
  hemotherapy: "Hemoterapia",
  rehabilitation: "Rehabilitación",
  cardiology: "Cardiología",
  custom: "Personalizado",
};

function localizePredefinedDocument(def, t) {
  const defaults = PREDEFINED_DEFAULTS[def.slug] || { title: "", description: "" };
  return {
    ...def,
    title: t(
      `app.consent.predefined.${def.slug}.title`,
      defaults.title
    ),
    description: t(
      `app.consent.predefined.${def.slug}.description`,
      defaults.description
    ),
    categoryKey: def.categoryKey,
    categoryLabel: t(
      `app.consent.categoryLabels.${def.categoryKey}`,
      CATEGORY_LABEL_DEFAULTS[def.categoryKey]
    ),
    isCustom: false,
  };
}

function localizeCustomDocument(doc, t) {
  return {
    ...doc,
    categoryKey: "custom",
    categoryLabel: t("app.consent.categoryLabels.custom", "Personalizado"),
    isCustom: true,
  };
}

export default function InformedConsent() {
  const { t } = useBrandTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showFilters] = useState(true);
  const [customDocuments, setCustomDocuments] = useState([]);

  useEffect(() => {
    if (user) {
      loadCustomDocuments();
    }
  }, [user]);

  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        loadCustomDocuments();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user]);

  const loadCustomDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("custom_consent_documents")
        .select("*")
        .eq("created_by", user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading custom documents:", error);
        return;
      }

      const transformedDocs = data.map((doc) => ({
        id: doc.id,
        title: doc.title,
        content: doc.content,
        category: doc.category,
        size: doc.size,
        createdBy: doc.created_by,
        createdAt: doc.created_at,
        updatedAt: doc.updated_at,
        icon: FileText,
        description: doc.description,
      }));

      setCustomDocuments(transformedDocs);
    } catch (error) {
      console.error("Error loading custom documents:", error);
    }
  };

  const predefinedDocuments = useMemo(
    () => CONSENT_DOCUMENT_DEFS.map((def) => localizePredefinedDocument(def, t)),
    [t]
  );

  const localizedCustomDocuments = useMemo(
    () => customDocuments.map((doc) => localizeCustomDocument(doc, t)),
    [customDocuments, t]
  );

  const filteredDocuments = useMemo(() => {
    let filtered;

    if (selectedCategory === "custom") {
      filtered = localizedCustomDocuments.filter(
        (doc) => doc.category === "Custom"
      );
    } else {
      // "Todas": primero los documentos propios del usuario, después los oficiales
      filtered =
        selectedCategory === "all"
          ? [...localizedCustomDocuments, ...predefinedDocuments]
          : [...predefinedDocuments];
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(term) ||
          doc.categoryLabel.toLowerCase().includes(term) ||
          (doc.description || "").toLowerCase().includes(term)
      );
    }

    if (selectedCategory !== "all" && selectedCategory !== "custom") {
      filtered = filtered.filter(
        (doc) => doc.categoryKey === selectedCategory
      );
    }

    return filtered;
  }, [
    searchTerm,
    selectedCategory,
    predefinedDocuments,
    localizedCustomDocuments,
  ]);

  const selectedCategoryLabel =
    selectedCategory === "all"
      ? ""
      : selectedCategory === "custom"
        ? t("app.consent.categoryLabels.custom", "Personalizado")
        : t(
            `app.consent.categoryLabels.${selectedCategory}`,
            CATEGORY_LABEL_DEFAULTS[selectedCategory] || selectedCategory
          );

  const resultsCountLabel =
    filteredDocuments.length === 1
      ? t("app.consent.documentsFoundOne", "{{count}} documento encontrado", {
          count: filteredDocuments.length,
        })
      : t("app.consent.documentsFoundMany", "{{count}} documentos encontrados", {
          count: filteredDocuments.length,
        });

  const handleDownload = (document) => {
    if (document.isCustom) {
      navigate(`/informed-consent/view/${document.id}`);
    } else {
      window.open(document.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDeleteCustom = async (documentId) => {
    if (
      confirm(
        t(
          "app.consent.confirmDeleteCustom",
          "¿Estás seguro de que quieres eliminar este documento personalizado?"
        )
      )
    ) {
      try {
        const { error } = await supabase
          .from("custom_consent_documents")
          .update({ is_active: false })
          .eq("id", documentId)
          .eq("created_by", user.id);

        if (error) {
          console.error("Error deleting document:", error);
          customToast(
            t("app.consent.deleteError", "Error al eliminar el documento"),
            "danger"
          );
          return;
        }

        await loadCustomDocuments();
        customToast(
          t("app.consent.deleteSuccess", "Documento eliminado exitosamente"),
          "success"
        );
      } catch (error) {
        console.error("Error deleting document:", error);
        customToast(
          t("app.consent.deleteError", "Error al eliminar el documento"),
          "danger"
        );
      }
    }
  };

  const handleEditCustom = (documentId) => {
    navigate(`/informed-consent/edit/${documentId}`);
  };

  const getCategoryColor = (categoryKey) =>
    CATEGORY_COLORS[categoryKey] || "bg-gray-100 text-gray-800";

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common.backHome", "Volver al Inicio")}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("app.consent.pageTitle", "Consentimiento Informado")}
          </h1>
          <p className="text-gray-600">
            {t(
              "app.consent.pageSubtitle",
              "Documentos oficiales de consentimiento informado para diferentes procedimientos médicos"
            )}
          </p>
        </div>

        <Card className="mb-6 pt-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              {showFilters && (
                <div className="">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={
                        selectedCategory === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setSelectedCategory("all")}
                    >
                      {t("app.consent.allCategories", "Todas las categorías")}
                    </Button>
                    <Button
                      variant={
                        selectedCategory === "custom" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setSelectedCategory("custom")}
                    >
                      {t("app.consent.myDocuments", "Mis documentos ({{count}})", {
                        count: customDocuments.length,
                      })}
                    </Button>
                    {PREDEFINED_CATEGORY_KEYS.map((categoryKey) => (
                      <Button
                        key={categoryKey}
                        variant={
                          selectedCategory === categoryKey
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedCategory(categoryKey)}
                      >
                        {t(
                          `app.consent.categoryLabels.${categoryKey}`,
                          CATEGORY_LABEL_DEFAULTS[categoryKey]
                        )}
                      </Button>
                    ))}

                    <div className="flex gap-2">
                      <Button
                        key="custom-create"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/informed-consent/create")}
                        className="!bg-green-600 !text-white !border-green-600 hover:!bg-green-700"
                      >
                        {t(
                          "app.consent.createCustomDoc",
                          "Crear Documento Personalizado"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mb-6">
          <p className="text-gray-600">
            {resultsCountLabel}
            {searchTerm &&
              t("app.consent.resultsForSearch", ' para "{{term}}"', {
                term: searchTerm,
              })}
            {selectedCategory !== "all" &&
              t("app.consent.resultsInCategory", ' en la categoría "{{category}}"', {
                category: selectedCategoryLabel,
              })}
          </p>
        </div>

        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedCategory === "custom"
                  ? t("app.consent.noCustomDocs", "No tienes documentos personalizados")
                  : t("app.consent.noDocsFound", "No se encontraron documentos")}
              </h3>
              <p className="text-gray-500 mb-4">
                {selectedCategory === "custom"
                  ? t(
                      "app.consent.noCustomDocsHint",
                      "Aún no has creado ningún documento de consentimiento personalizado."
                    )
                  : t(
                      "app.consent.adjustFiltersHint",
                      "Intenta ajustar tus filtros de búsqueda o buscar con otros términos."
                    )}
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("all");
                  }}
                >
                  {t("app.consent.clearFilters", "Limpiar filtros")}
                </Button>
                {selectedCategory === "custom" && (
                  <Button
                    onClick={() => navigate("/informed-consent/create")}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("app.consent.createFirstDoc", "Crear Primer Documento")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((document) => (
              <DocumentCard
                key={`${document.isCustom ? "custom" : "official"}-${document.id}`}
                document={document}
                onDownload={handleDownload}
                getCategoryColor={getCategoryColor}
                onEdit={handleEditCustom}
                onDelete={handleDeleteCustom}
              />
            ))}
          </div>
        )}

        <Card className="mt-8 mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t("app.consent.importantInfoTitle", "Información Importante")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-2">
                <h4 className="font-medium">
                  {t("app.consent.aboutDocs", "Sobre los Documentos")}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>
                    {t(
                      "app.consent.helpPdf",
                      "Todos los documentos están en formato PDF"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.officialHealthDocs",
                      "Son documentos oficiales del sistema de salud argentino"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.helpBeforeProcedure",
                      "Deben ser completados antes de cada procedimiento"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.helpReadFully",
                      "Es importante leer completamente antes de firmar"
                    )}
                  </li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">
                  {t("app.consent.recommendedUse", "Uso Recomendado")}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>
                    {t(
                      "app.consent.downloadForProcedure",
                      "Descargar el documento correspondiente al procedimiento"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.helpReadCarefully",
                      "Leer detenidamente toda la información"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.helpAskDoctor",
                      "Consultar dudas con el profesional médico"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.consent.helpSignWithDoctor",
                      "Completar y firmar en presencia del médico"
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DocumentCard({
  document,
  onDownload,
  getCategoryColor,
  onEdit,
  onDelete,
}) {
  const { t } = useBrandTranslation();
  const IconComponent = document.icon || FileText;

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-blue-50 rounded-lg">
              <IconComponent className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg leading-tight mb-2 min-h-[45px]">
                {document.title}
              </CardTitle>
              <Badge
                className={`text-xs ${getCategoryColor(document.categoryKey)}`}
              >
                {document.categoryLabel}
              </Badge>
            </div>
          </div>
          {document.isCustom && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(document.id)}
                className="p-1 h-8 w-8"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(document.id)}
                className="p-1 h-8 w-8 text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 leading-relaxed h-[45px]">
          {document.description}
        </p>

        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span>PDF • {document.size}</span>
          </div>
        </div>

        <Button
          onClick={() => onDownload(document)}
          className="w-full"
          size="sm"
        >
          <Download className="w-4 h-4 mr-2" />
          {t("app.consent.downloadDocument", "Descargar Documento")}
          <ExternalLink className="w-3 h-3 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
